import { getHomeGroupAccessRank } from "src/data/homeGroups/logic/homeGroupsAccess"
import {
  HomeGroupRank,
  THomeGroup,
} from "src/data/homeGroups/types/homeGroupTypes"
import { getOrgAccessRank } from "src/data/organizations/logic/organizationAccess"
import { TOrganizationRole } from "src/data/organizations/types/organizationMemberTypes"
import { OrganizationRank } from "src/data/organizations/types/organizationMemberTypes"
import {
  AccessLevelRank,
  RestrictedFeature,
} from "src/data/user/types/accessTypes"
import { UserRoles } from "src/data/user/user"
import { arrayIncludesValueOfOtherArray } from "src/utils/genericUtil"

type THomeGroupRole = THomeGroup["user_role"]

/** Check whether a given rank is allowed access to a feature */
export function allowedTo(
  feature: RestrictedFeature,
  rank: AccessLevelRank
): boolean {
  switch (feature) {
    case RestrictedFeature.TRANSFER_HOME_OWNERSHIP:
    case RestrictedFeature.DELETE_HOME:
    case RestrictedFeature.PAY_FOR_PLAN:
    case RestrictedFeature.REMOVE_MEMBERS:
    case RestrictedFeature.MAKE_ADMIN:
    case RestrictedFeature.OWNER_FEATURE:
      return rank >= AccessLevelRank.OWNER

    case RestrictedFeature.CHANGE_CONFIG:
    case RestrictedFeature.INVITE_MEMBERS:
    case RestrictedFeature.ADMIN_FEATURE:
      return rank >= AccessLevelRank.ADMIN

    case RestrictedFeature.VIEW_ALL_HOME_DATA:
    case RestrictedFeature.TOGGLE_SECURITY:
    case RestrictedFeature.ACCESS_CHAT_GROUPS:
      return rank >= AccessLevelRank.MEMBER
  }
  throw Error("Invalid feature") // If making an invalid call from regular JS
}

export function toggleSecurityAlarmAllowed(rank: AccessLevelRank | undefined) {
  const accessLevel = rank || 0
  return allowedTo(RestrictedFeature.TOGGLE_SECURITY, accessLevel)
}

export function toggleNoiseMonitoringAllowed(
  rank: AccessLevelRank | undefined
) {
  const accessLevel = rank || 0
  return allowedTo(RestrictedFeature.CHANGE_CONFIG, accessLevel)
}

export function toggleWaterLeakDetectionAllowed(
  rank: AccessLevelRank | undefined
) {
  const accessLevel = rank || 0
  return allowedTo(RestrictedFeature.CHANGE_CONFIG, accessLevel)
}

export function toggleSmokingDetectionAllowed(
  rank: AccessLevelRank | undefined
) {
  const accessLevel = rank || 0
  return allowedTo(RestrictedFeature.CHANGE_CONFIG, accessLevel)
}

export function toggleFireCoAlarmAllowed(rank: AccessLevelRank | undefined) {
  const accessLevel = rank || 0
  return allowedTo(RestrictedFeature.CHANGE_CONFIG, accessLevel)
}

const OrgAccessLogicBase = {
  hasOwnerAccess(role?: TOrganizationRole) {
    return getOrgAccessRank(role) >= OrganizationRank.OWNER
  },
  hasAdminAccess(role?: TOrganizationRole) {
    return getOrgAccessRank(role) >= OrganizationRank.ADMIN
  },
  hasMemberAccess(role?: TOrganizationRole) {
    return getOrgAccessRank(role) >= OrganizationRank.MEMBER
  },
  hasLimitedAccess(role?: TOrganizationRole) {
    return getOrgAccessRank(role) >= OrganizationRank.MINIMUM
  },
}

/**
 * Low-level function to determine org access levels via an OrganizationRole.
 *
 * The role param can come either from IOrganization.user_role or
 * IHome.user_role; note that user_role is context dependent on the user that
 * made the query to retrieve the object.
 *
 * In future iterations, we may also want to take the current plan and potential
 * violations into account.
 *
 * @deprecated For **organization-scoped** checks only: use `usePermissions`
 * instead, and add the entity to its permission matrix if it is missing. This is
 * the low-level role-to-rank helper `usePermissions` is built on, and calling it
 * from a component spreads role arithmetic through the UI.
 *
 * Still the right tool when the role is a **home's** `user_role` rather than the
 * organization's, which is most of the calls today. `usePermissions` takes no
 * home id and cannot express that check, so those call sites have nowhere to
 * move to yet.
 */
export function getAccessLogic({ role }: { role?: TOrganizationRole }) {
  const hasOwnerAccess = OrgAccessLogicBase.hasOwnerAccess(role)
  const hasAdminAccess = OrgAccessLogicBase.hasAdminAccess(role)
  const hasMemberAccess = OrgAccessLogicBase.hasMemberAccess(role)
  const hasLimitedAccess = OrgAccessLogicBase.hasLimitedAccess(role)

  return {
    hasOwnerAccess,
    hasAdminAccess,
    hasMemberAccess,
    hasLimitedAccess,
  }
}

const homeGroupAccessLogicBase = {
  hasAdminAccess(role?: THomeGroupRole) {
    return getHomeGroupAccessRank(role) >= HomeGroupRank.ADMIN
  },
  hasMemberAccess(role?: THomeGroupRole) {
    return getHomeGroupAccessRank(role) >= HomeGroupRank.MEMBER
  },
}

/**
 * Low-level function to determine home group access levels from the HomeGroupRole.
 */
export function getHomeGroupAccessLogic({
  role,
}: {
  role?: THomeGroup["user_role"]
}) {
  const hasAdminAccess = homeGroupAccessLogicBase.hasAdminAccess(role)
  const hasMemberAccess = homeGroupAccessLogicBase.hasMemberAccess(role)
  return {
    hasAdminAccess,
    hasMemberAccess,
  }
}

export const rolesWithParadiseAccess = [
  UserRoles.SUPERADMIN,
  UserRoles.SALES_ADMIN,
] as const

export function hasAccessToParadise(userRoles: UserRoles[] | string[]) {
  return arrayIncludesValueOfOtherArray(rolesWithParadiseAccess, userRoles)
}

export function isSuperAdmin(userRoles: UserRoles[] | string[]) {
  return userRoles.includes(UserRoles.SUPERADMIN)
}
